import React, { useState } from 'react';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Button } from '@progress/kendo-react-buttons';
import { downloadIcon } from '@progress/kendo-svg-icons';
import { useGetDocumentVersions } from '../../context/versions/useGetDocumentVersions';
import { useCurrentUserContext } from '../../context/currentUser/CurrentUserContext';
import { DocumentVersionResponse } from '../../Model/responseDtos/DocumentVersionResponse';
import { useGetDocumentVersionMetadata } from '../../context/versions/useGetDocumentVersionMetadata';
import { buildDocumentVersionEndpointPath } from '../../context/versions/VersionsService';
import { API_URL } from '../../config/consts';
import { useGlobalizationContext } from '../../context/globalization/GlobalizationContext';
import { useTranslation } from '../../context/globalization/useTranslation';
import { useGetContentType } from '../../context/contentTypes/useGetContentType';
import { useGetAllFields } from '../../context/fields/useGetAllFields';
import { Loader } from '@progress/kendo-react-indicators';

interface DocumentVersionViewerProps {
  isOpen: boolean;
  onClose: () => void;
  documentId: string;
  documentName: string;
  contentType: string;
}

export const DocumentVersionViewer: React.FC<DocumentVersionViewerProps> = ({
  isOpen,
  onClose,
  documentId,
  documentName,
  contentType
}) => {
  const { selectedIndex } = useCurrentUserContext();
  const [selectedVersion, setSelectedVersion] = useState<DocumentVersionResponse | null>(null);
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);

  const { data: contentTypeData } = useGetContentType(contentType, selectedLocale);
  const { data: allFields } = useGetAllFields(selectedLocale, selectedIndex?.id);

  const {
    data: versions = [],
    isLoading,
    error
  } = useGetDocumentVersions(
    {
      documentId,
      ctName: contentType,
      indexId: selectedIndex?.id ?? -1
    },
    {
      enabled: isOpen && selectedIndex?.id != null
    }
  );

  const { data: versionMetadata, isLoading: isLoadingMetadata } = useGetDocumentVersionMetadata(
    {
      documentId,
      ctName: contentType,
      indexId: selectedIndex?.id ?? -1,
      versionId: selectedVersion?.versionId || ''
    },
    { enabled: isOpen && !!selectedVersion?.versionId }
  );

  const getDownloadUrl = (version: DocumentVersionResponse) => {
    if (version.versionId && selectedIndex) {
      return `${API_URL}/${buildDocumentVersionEndpointPath(contentType, documentId, version.versionId, selectedIndex.id, false)}`;
    }

    return undefined;
  };

  const handleClose = () => {
    setSelectedVersion(null);
    onClose();
  };

  const getFieldDisplayName = (fieldName: string): string => {
    const fieldTranslation = allFields?.find(field => field.name === fieldName)?.displayName;
    if (fieldTranslation) {
      return fieldTranslation;
    }

    const contentTypeField = contentTypeData?.fields?.find(field => field.name === fieldName);
    if (contentTypeField?.name) {
      return contentTypeField.name;
    }

    return fieldName;
  };

  const getDisplayableMetadata = (metadata: Record<string, any>) => {
    if (!metadata) {
      return [];
    }

    return Object.entries(metadata)
      .filter(([, value]) => value !== undefined && value !== null)
      .map(([key, value]) => ({
        key,
        displayName: getFieldDisplayName(key),
        value: String(value)
      }));
  };

  const formatDateForDisplay = (dateString: string | Date | undefined): string => {
    if (!dateString) {
      return '';
    }

    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return '';
    }

    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    const seconds = String(date.getUTCSeconds()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds} UTC`;
  };

  return isOpen ? (
    <Dialog
      onClose={handleClose}
      className="documentVersionViewerWindow"
      closeIcon={true}
      title={translate('grid.versionViewer.dialogTitle')}
    >
      <>
        <div className="headerSection">
          <h2>
            {translate('grid.versionViewer.versionHistoryTitle')}
            {documentName}
          </h2>
          <p>
            {translate('grid.versionViewer.documentIdLabel')}
            {documentId}
          </p>
        </div>

        <div className="mainContent">
          <div className="leftPanel">
            <div className="leftPanelHeader">
              <h3>{translate('grid.versionViewer.availableVersionsTitle')}</h3>
              <p>
                {versions?.length ?? 0}
                {translate('grid.versionViewer.versionsFoundSuffix')}
              </p>
            </div>

            <div className="versionList">
              {isLoading ? (
                <div className="emptyState">
                  <p>{translate('grid.versionViewer.loadingVersionsMessage')}</p>
                </div>
              ) : error ? (
                <div className="emptyState">
                  <p>{translate('grid.versionViewer.errorLoadingVersionsMessage')}</p>
                </div>
              ) : versions.length === 0 ? (
                <div className="emptyState">
                  <p>{translate('grid.versionViewer.noVersionsFoundMessage')}</p>
                </div>
              ) : (
                versions.map((version: DocumentVersionResponse) => (
                  <div
                    key={version.versionId}
                    className={`versionCard ${selectedVersion?.versionId === version.versionId ? 'selected' : ''} ${
                      selectedVersion?.versionId === version.versionId && isLoadingMetadata ? 'loading' : ''
                    }`}
                    onClick={() => setSelectedVersion(version)}
                  >
                    <div className="versionCardHeader">
                      <div className="versionTitle">
                        <h4>{formatDateForDisplay(version.versionId)}</h4>
                        {selectedVersion?.versionId === version.versionId && isLoadingMetadata && (
                          <Loader size="small" type="pulsing" />
                        )}
                      </div>
                      <div>
                        <a
                          href={getDownloadUrl(version)}
                          download={`${documentName}_${version.versionId}`}
                          onClick={e => e.stopPropagation()}
                        >
                          <Button size="small" fillMode="flat" svgIcon={downloadIcon} />
                        </a>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="rightPanel">
            {selectedVersion ? (
              <>
                {isLoadingMetadata ? (
                  <div className="loadingState">
                    <Loader size="medium" type="converging-spinner" />
                    <p>{translate('grid.versionViewer.loadingMetadataMessage')}</p>
                  </div>
                ) : (
                  <>
                    <div className="rightPanelHeader">
                      {versionMetadata?.metadata ? (
                        <div className="metadataSection">
                          <h4>{translate('grid.versionViewer.metadataTitle')}</h4>
                          <div className="detailsGrid">
                            {getDisplayableMetadata(versionMetadata.metadata).map(({ key, displayName, value }) => (
                              <div key={key}>
                                <span title={key}>{displayName}: </span>
                                <span>{value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </div>

                    <div className="downloadSection">
                      <p>{translate('grid.versionViewer.downloadInstructionMessage')}</p>
                      <a
                        href={getDownloadUrl(selectedVersion)}
                        download={`${documentName}_${selectedVersion.versionId}`}
                      >
                        <Button size="large" themeColor="primary" svgIcon={downloadIcon}>
                          {translate('grid.versionViewer.downloadVersionButtonText')}
                        </Button>
                      </a>
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="emptyState">
                <p>{translate('grid.versionViewer.selectVersionMessage')}</p>
              </div>
            )}
          </div>
        </div>
      </>
    </Dialog>
  ) : null;
};
